import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Platform } from 'react-native'
import * as SecureStore from 'expo-secure-store'

import {
  configureRuntimeEnvironment,
  DEFAULT_RUNTIME_ENVIRONMENT,
  getRuntimeConfig,
  getRuntimeEnvironment,
  RUNTIME_ENVIRONMENTS,
  type ActiveRuntimeEnvironment,
  type RuntimeEnvironment
} from '@/constants/config'

const ENVIRONMENT_KEY = 'baobi_runtime_environment'
const NATIVE_AUTH_KEY = 'ybb_auth'
const WEB_AUTH_KEY = 'ybb_main_auth'

interface RuntimeEnvironmentContextValue {
  environment: ActiveRuntimeEnvironment
  selectEnvironment: (environment: RuntimeEnvironment) => Promise<void>
}

const RuntimeEnvironmentContext = createContext<RuntimeEnvironmentContextValue>({
  environment: DEFAULT_RUNTIME_ENVIRONMENT,
  selectEnvironment: async () => undefined
})

function isRuntimeEnvironment(value: string | null): value is RuntimeEnvironment {
  return Boolean(value && value in RUNTIME_ENVIRONMENTS)
}

async function loadEnvironment() {
  try {
    if (Platform.OS === 'web') {
      return globalThis.localStorage.getItem(ENVIRONMENT_KEY)
    }
    return await SecureStore.getItemAsync(ENVIRONMENT_KEY)
  } catch {
    return null
  }
}

async function persistEnvironment(environment: RuntimeEnvironment) {
  if (Platform.OS === 'web') {
    globalThis.localStorage.setItem(ENVIRONMENT_KEY, environment)
    globalThis.sessionStorage.removeItem(WEB_AUTH_KEY)
    return
  }

  await Promise.allSettled([
    SecureStore.setItemAsync(ENVIRONMENT_KEY, environment),
    SecureStore.deleteItemAsync(NATIVE_AUTH_KEY)
  ])
}

export function RuntimeEnvironmentProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false)
  const [environment, setEnvironment] = useState<ActiveRuntimeEnvironment>(getRuntimeEnvironment())

  useEffect(() => {
    loadEnvironment().then((storedEnvironment) => {
      const selectedEnvironment = isRuntimeEnvironment(storedEnvironment)
        ? storedEnvironment
        : DEFAULT_RUNTIME_ENVIRONMENT
      configureRuntimeEnvironment(selectedEnvironment)
      logRuntimeEnvironment(selectedEnvironment)
      setEnvironment(selectedEnvironment)
      setReady(true)
    })
  }, [])

  const value = useMemo<RuntimeEnvironmentContextValue>(
    () => ({
      environment,
      selectEnvironment: async (selectedEnvironment) => {
        await persistEnvironment(selectedEnvironment)
        configureRuntimeEnvironment(selectedEnvironment)
        logRuntimeEnvironment(selectedEnvironment)
        setEnvironment(selectedEnvironment)
      }
    }),
    [environment]
  )

  if (!ready) return null

  return (
    <RuntimeEnvironmentContext.Provider value={value}>
      <RuntimeEnvironmentTree key={environment}>{children}</RuntimeEnvironmentTree>
    </RuntimeEnvironmentContext.Provider>
  )
}

function logRuntimeEnvironment(environment: ActiveRuntimeEnvironment) {
  console.info('RUNTIME_ENVIRONMENT_CONFIGURED', {
    environment,
    apiHost: new URL(getRuntimeConfig().apiUrl).host
  })
}

function RuntimeEnvironmentTree({ children }: { children: ReactNode }) {
  return <>{children}</>
}

export function useRuntimeEnvironment() {
  return useContext(RuntimeEnvironmentContext)
}
